/**
 * CockpitClassLessonPage — detail view for a single weekly lesson.
 *
 * Free tier sees teaser + paywall CTA; paid tier sees full body.
 * Below the body: thumbs up/down, "Would you use this?" 3-option poll,
 * and a free-text "what more needs building" box. All submitted via
 * POST /api/cockpit-class/lessons/:slug/feedback.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import {
  GraduationCap, ArrowLeft, Lock, ThumbsUp, ThumbsDown,
  CheckCircle2, Send, Sparkles,
} from 'lucide-react';
import { DepartmentNav } from '../components/DepartmentNav';
import { API_URL } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// Minimal markdown renderer — matches the tiny shim the backend email uses.
// Handles: ### h3, ## h2, **bold**, *italic*, "- " bullet lists, blank-line paragraphs.
const renderMarkdown = (md) => {
  if (!md) return null;
  const lines = md.split(/\n/);
  const blocks = [];
  let current = null;
  const flush = () => {
    if (current) {
      blocks.push(current);
      current = null;
    }
  };

  for (const raw of lines) {
    const line = raw;
    if (/^### (.+)/.test(line)) {
      flush();
      blocks.push({ type: 'h3', text: line.replace(/^### /, '') });
    } else if (/^## (.+)/.test(line)) {
      flush();
      blocks.push({ type: 'h2', text: line.replace(/^## /, '') });
    } else if (/^- /.test(line)) {
      if (!current || current.type !== 'ul') { flush(); current = { type: 'ul', items: [] }; }
      current.items.push(line.replace(/^- /, ''));
    } else if (line.trim() === '') {
      flush();
    } else {
      if (!current || current.type !== 'p') { flush(); current = { type: 'p', text: line }; }
      else current.text += ' ' + line;
    }
  }
  flush();

  const inline = (text) => {
    // Split on bold + italic markers
    const pieces = [];
    let remaining = text;
    const rx = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
    let lastIdx = 0;
    let m;
    while ((m = rx.exec(text)) !== null) {
      if (m.index > lastIdx) pieces.push({ t: 'text', v: text.slice(lastIdx, m.index) });
      const tok = m[0];
      if (tok.startsWith('**')) pieces.push({ t: 'b', v: tok.slice(2, -2) });
      else pieces.push({ t: 'i', v: tok.slice(1, -1) });
      lastIdx = m.index + tok.length;
    }
    if (lastIdx < text.length) pieces.push({ t: 'text', v: text.slice(lastIdx) });
    if (!pieces.length) return text;
    return pieces.map((p, i) => {
      if (p.t === 'b') return <b key={i}>{p.v}</b>;
      if (p.t === 'i') return <i key={i}>{p.v}</i>;
      return <React.Fragment key={i}>{p.v}</React.Fragment>;
    });
    // eslint-disable-next-line no-unreachable
    return remaining;
  };

  return blocks.map((b, i) => {
    if (b.type === 'h3') return <h3 key={i} className="text-lg font-semibold mt-6 mb-2 text-foreground">{inline(b.text)}</h3>;
    if (b.type === 'h2') return <h2 key={i} className="text-xl font-semibold mt-8 mb-3 text-foreground">{inline(b.text)}</h2>;
    if (b.type === 'ul') return <ul key={i} className="list-disc pl-5 my-3 space-y-1 text-foreground/85">{b.items.map((it, j) => <li key={j}>{inline(it)}</li>)}</ul>;
    return <p key={i} className="my-3 leading-relaxed text-foreground/85">{inline(b.text)}</p>;
  });
};

export const CockpitClassLessonPage = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, lesson: null, err: null });
  const [vote, setVote] = useState(null);
  const [poll, setPoll] = useState(null);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/cockpit-class/lessons/${slug}`, { withCredentials: true });
        if (!alive) return;
        setState({ loading: false, lesson: r.data, err: null });
      } catch (e) {
        if (!alive) return;
        const status = e?.response?.status;
        setState({ loading: false, lesson: null, err: status === 404 ? 'not_found' : 'fetch_failed' });
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  const submit = async () => {
    if (!user) {
      toast.error('Sign in to leave feedback');
      return;
    }
    if (!vote && !poll && !text.trim()) {
      toast.error('Pick a vote, a poll option, or write something');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/cockpit-class/lessons/${slug}/feedback`,
        { vote, poll, text: text.trim() },
        { withCredentials: true },
      );
      setSubmitted(true);
      toast.success('Thanks — your feedback lands on the roadmap.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen bg-background">
        <DepartmentNav />
        <main className="max-w-3xl mx-auto p-6 space-y-4">
          <Skeleton className="h-6 w-24 rounded" />
          <Skeleton className="h-10 w-full rounded" />
          <Skeleton className="h-40 w-full rounded" />
        </main>
      </div>
    );
  }

  if (state.err || !state.lesson) {
    return (
      <div className="min-h-screen bg-background">
        <DepartmentNav />
        <main className="max-w-3xl mx-auto p-6">
          <Card data-testid="cockpit-class-lesson-missing">
            <CardContent className="py-16 text-center space-y-4">
              <p className="text-muted-foreground">This lesson isn't available.</p>
              <Button onClick={() => navigate('/cockpit/class')} variant="outline" data-testid="cockpit-class-back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to class archive
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const lesson = state.lesson;
  const isPaywalled = lesson.paywalled;
  const counts = lesson.counts || {};

  return (
    <div data-testid="cockpit-class-lesson-page" className="min-h-screen bg-background">
      <DepartmentNav />
      <main className="max-w-3xl mx-auto p-6 space-y-6">
        <Link to="/cockpit/class" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition">
          <ArrowLeft className="w-4 h-4" /> All classes
        </Link>

        {/* Header */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-600 font-semibold">
            <GraduationCap className="w-4 h-4" /> Super Cockpit · Week's class
            {lesson.metric_tag && (
              <Badge variant="outline" className="ml-2 text-[10px] border-emerald-500/40 text-emerald-700 dark:text-emerald-300">
                {lesson.metric_tag}
              </Badge>
            )}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight flex items-start gap-3">
            <span className="text-5xl leading-none">{lesson.hero_emoji}</span>
            <span>{lesson.title}</span>
          </h1>
          {lesson.dek && <p className="text-lg text-muted-foreground leading-relaxed">{lesson.dek}</p>}
        </div>

        {/* Body */}
        <Card>
          <CardContent className="pt-6">
            {lesson.teaser_md && (
              <div className="mb-4 pb-4 border-b border-border/60 text-base text-foreground/90 leading-relaxed">
                {lesson.teaser_md}
              </div>
            )}
            {isPaywalled ? (
              <div className="py-8 text-center space-y-4" data-testid="cockpit-class-paywall">
                <Lock className="w-10 h-10 mx-auto text-muted-foreground opacity-40" />
                <div className="space-y-2">
                  <div className="text-sm uppercase tracking-widest text-emerald-600 font-semibold">Paid members only</div>
                  <h3 className="text-xl font-semibold">Unlock the full lesson</h3>
                  <p className="text-sm text-muted-foreground max-w-md mx-auto">
                    The five-beat breakdown — what the metric is, why supers care, how we
                    compute it, the call it triggers, and the trap — is a Pro-tier perk.
                  </p>
                </div>
                <Link to="/profile">
                  <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="cockpit-class-upgrade-btn">
                    <Sparkles className="w-4 h-4 mr-2" /> Upgrade to read the full class
                  </Button>
                </Link>
              </div>
            ) : (
              <article className="prose-sm max-w-none" data-testid="cockpit-class-body">
                {renderMarkdown(lesson.body_md)}
              </article>
            )}
          </CardContent>
        </Card>

        {/* Feedback */}
        {!submitted && (
          <Card data-testid="cockpit-class-feedback">
            <CardHeader>
              <CardTitle className="text-lg">Your turn</CardTitle>
              <CardDescription>
                {lesson.action_prompt || 'What should we build next? Drop a quick vote and a note below.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Thumbs */}
              <div className="flex items-center gap-3">
                <Button
                  variant={vote === 'up' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setVote(vote === 'up' ? null : 'up')}
                  data-testid="cockpit-feedback-thumbs-up"
                  className={vote === 'up' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                >
                  <ThumbsUp className="w-4 h-4 mr-2" /> Helpful {counts.up ? `· ${counts.up}` : ''}
                </Button>
                <Button
                  variant={vote === 'down' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setVote(vote === 'down' ? null : 'down')}
                  data-testid="cockpit-feedback-thumbs-down"
                  className={vote === 'down' ? 'bg-red-600 hover:bg-red-700' : ''}
                >
                  <ThumbsDown className="w-4 h-4 mr-2" /> Missed {counts.down ? `· ${counts.down}` : ''}
                </Button>
              </div>

              {/* Poll */}
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">
                  Would you use this in your routine?
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: 'use', label: 'Yes, immediately' },
                    { key: 'not_yet', label: 'Not yet — need more' },
                    { key: 'already', label: 'Already do this' },
                  ].map(({ key, label }) => (
                    <Button
                      key={key}
                      variant={poll === key ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPoll(poll === key ? null : key)}
                      data-testid={`cockpit-feedback-poll-${key}`}
                      className={poll === key ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Free-text */}
              <div className="space-y-2">
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                  What should we build next?
                </div>
                <Textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="e.g., I want per-zone MAD for my front vs back yard since my front dries 2× faster."
                  rows={3}
                  maxLength={2000}
                  data-testid="cockpit-feedback-text"
                />
              </div>

              <Button
                onClick={submit}
                disabled={submitting || !user}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="cockpit-feedback-submit"
              >
                <Send className="w-4 h-4 mr-2" />
                {submitting ? 'Sending…' : 'Send to the roadmap'}
              </Button>
              {!user && (
                <p className="text-xs text-muted-foreground">
                  <Link to="/" className="underline">Sign in</Link> to leave feedback — your vote lands on Derwin's roadmap board.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {submitted && (
          <Card data-testid="cockpit-class-feedback-thanks" className="border-emerald-500/40 bg-emerald-50 dark:bg-emerald-950/30">
            <CardContent className="py-8 text-center space-y-2">
              <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-600" />
              <h3 className="text-lg font-semibold">On the roadmap.</h3>
              <p className="text-sm text-muted-foreground">
                Your note is in the admin board — every Monday morning it steers what we ship that week.
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default CockpitClassLessonPage;
